/*!
 * Copyright (c) Trimble Inc. All rights reserved
 */

import { Injectable } from '@angular/core';
import { DotNetRequestMessageService } from '@mep/web-browser-bridge';
import { Observable, from } from 'rxjs';
import { LocalStorageService } from '../local-storage/local-storage.service';
import { GlobalConstants } from '../../../../feature/src/lib/constants/global-constants';
import {
  Annotation,
  AnnotationSettings,
  AnnotationSettingsTabType,
  CurrentSelectionResult,
  RevitInformation,
  TagsState
} from '../../../../feature/src/lib/models/annotation-settings';

@Injectable({
  providedIn: 'root'
})
export class AnnotationSettingsService {
  constructor(private messageHandler: DotNetRequestMessageService, private localStorageService: LocalStorageService) {}

  // Get all the data from the xml file
  public getAnnotationSettings(): Observable<AnnotationSettings> {
    return from<Promise<AnnotationSettings>>(
      this.messageHandler.sendRequestAsync(GlobalConstants.GET_ANNOTATION_SETTINGS_INFO_METHOD_NAME)
    );
  }

  public saveAnnotationSettings(annotationSettings: AnnotationSettings): Observable<void> {
    return from<Promise<void>>(
      this.messageHandler.sendRequestAsync(GlobalConstants.SAVE_ANNOTATION_SETTINGS_INFO_METHOD_NAME, [
        annotationSettings
      ])
    );
  }

  public getRevitInformation(): Observable<RevitInformation> {
    return from<Promise<RevitInformation>>(
      this.messageHandler.sendRequestAsync(GlobalConstants.GET_REVIT_INFORMATION_METHOD_NAME)
    );
  }

  public getTagLocationPath(): Observable<string> {
    return from<Promise<string>>(
      this.messageHandler.sendRequestAsync(GlobalConstants.GET_TAG_LOCATION_PATH_METHOD_NAME)
    );
  }

  public isCurrentSelectionValid(): Observable<CurrentSelectionResult> {
    return from<Promise<CurrentSelectionResult>>(
      this.messageHandler.sendRequestAsync(GlobalConstants.IS_CURRENT_SELECTION_VALID_METHOD_NAME)
    );
  }

  public applyData(selectedTab: AnnotationSettingsTabType): Observable<void> {
    return from<Promise<void>>(
      this.messageHandler.sendRequestAsync(GlobalConstants.APPLY_DATA_METHOD_NAME, [selectedTab])
    );
  }

  public getCableTrayDataFromXml(): Annotation[] {
    const dataFromXml = this.localStorageService.getStorage(
      GlobalConstants.ANNOTATION_SETTINGS_DATA_FROM_XML_NAME
    ) as AnnotationSettings;
    return dataFromXml.cableTrayAnnotation;
  }

  public getConduitSettingsDataFromXml(): Annotation[] {
    const dataFromXml = this.localStorageService.getStorage(
      GlobalConstants.ANNOTATION_SETTINGS_DATA_FROM_XML_NAME
    ) as AnnotationSettings;
    return dataFromXml.conduitAnnotation;
  }

  public getActivePropertyByTagName(name: string, items: Annotation[]): boolean {
    if (name === '' || items == null) {
      return false;
    }
    switch (name) {
      case GlobalConstants.SIZE_TAG_NAME:
        return items.find(x => x?.annotationName === GlobalConstants.SIZE_ANNOTATION_NAME)?.active ===
          GlobalConstants.TRUE
          ? true
          : false;

      case GlobalConstants.ELEVATION_TAG_NAME:
        return items.find(x => x?.annotationName === GlobalConstants.ELEVATION_ANNOTATION_NAME)?.active ===
          GlobalConstants.TRUE
          ? true
          : false;

      case GlobalConstants.ITEM_NUMBER_TAG_NAME:
        return items.find(x => x?.annotationName === GlobalConstants.ITEM_NUMBER_ANNOTATION_NAME)?.active ===
          GlobalConstants.TRUE
          ? true
          : false;

      case GlobalConstants.SPOOL_TAG_NAME:
        return items.find(x => x?.annotationName === GlobalConstants.SPOOL_ANNOTATION_NAME)?.active ===
          GlobalConstants.TRUE
          ? true
          : false;

      default:
        return false;
    }
  }

  public getTagDataFromXmlByTabNameAndAnnotationName(tabName: string, tagName: string): Annotation {
    if (tabName === '' || tagName === '') {
      return new Annotation(tabName, tagName, '2024');
    }
    const dataFromXml = this.localStorageService.getStorage(
      GlobalConstants.ANNOTATION_SETTINGS_DATA_FROM_XML_NAME
    ) as AnnotationSettings;
    const tagDataFromXml =
      tabName === GlobalConstants.CONDUIT_SETTINGS_TAB_NAME
        ? dataFromXml.conduitAnnotation
        : dataFromXml.cableTrayAnnotation;
    const revitVersion = this.localStorageService.getStorage(GlobalConstants.REVIT_VERSION) as string;

    switch (tagName) {
      case GlobalConstants.SIZE_ANNOTATION_NAME:
        return (
          tagDataFromXml.find(x => x.annotationName === GlobalConstants.SIZE_ANNOTATION_NAME) ?? 
          new Annotation(tabName, tagName, revitVersion)
        );

      case GlobalConstants.ELEVATION_ANNOTATION_NAME:
        return (
          tagDataFromXml.find(x => x.annotationName === GlobalConstants.ELEVATION_ANNOTATION_NAME) ??
          new Annotation(tabName, tagName, revitVersion)
        );

      case GlobalConstants.ITEM_NUMBER_ANNOTATION_NAME:
        return (
          tagDataFromXml.find(x => x.annotationName === GlobalConstants.ITEM_NUMBER_ANNOTATION_NAME) ??
          new Annotation(tabName, tagName, revitVersion)
        );

      case GlobalConstants.SPOOL_ANNOTATION_NAME:
        return (
          tagDataFromXml.find(x => x.annotationName === GlobalConstants.SPOOL_ANNOTATION_NAME) ??
          new Annotation(tabName, tagName, revitVersion)
        );

      default:
        return new Annotation(tabName, tagName, revitVersion);
    }
  }

  public getTagDataFromLocalStorageByTagName(tagName: string): Annotation {
    if (tagName === '') {
      return new Annotation('', tagName, '');
    }
    return this.localStorageService.getStorage(tagName) as Annotation;
  }

  public getOccurrencePropertyByName(name: string, data: Annotation): boolean {
    if (name === '' || data == null) {
      return false;
    }
    return data?.option === name;
  }

  public getPropertiesPropertyByName(name: string, data: Annotation): boolean {
    if (name == '' || data == null) {
      return false;
    }
    switch (name) {
      case GlobalConstants.SHOW_LEADER:
        return data.showLeader === GlobalConstants.TRUE ? true : false;

      case GlobalConstants.HORIZONTAL_TEXT:
        return data.horizontal === GlobalConstants.TRUE ? true : false;

      case GlobalConstants.SHOW_BORDER:
        return data.showBorder === GlobalConstants.TRUE ? true : false;

      default:
        return false;
    }
  }

  public setActiveValueToLocalStorageByTabNameAndTagName(tabName: string, tagName: string, value: string): void {
    if (tabName === '' || tagName === '' || value === '') {
      return;
    }
    switch (tabName) {
      case GlobalConstants.CABLE_TRAY_TAB_NAME:
        switch (tagName) {
          case GlobalConstants.SIZE_TAG_NAME:
            this.setActiveInStorageBySelection(GlobalConstants.CABLE_TRAY_SIZE_DATA_SELECTION_NAME, value);
            break;

          case GlobalConstants.ELEVATION_TAG_NAME:
            this.setActiveInStorageBySelection(GlobalConstants.CABLE_TRAY_ELEVATION_DATA_SELECTION_NAME, value);
            break;

          case GlobalConstants.ITEM_NUMBER_TAG_NAME:
            this.setActiveInStorageBySelection(GlobalConstants.CABLE_TRAY_ITEM_NUMBER_DATA_SELECTION_NAME, value);
            break;

          case GlobalConstants.SPOOL_TAG_NAME:
            this.setActiveInStorageBySelection(GlobalConstants.CABLE_TRAY_SPOOL_DATA_SELECTION_NAME, value);
            break;

          default:
            break;
        }
        break;
      case GlobalConstants.CONDUIT_SETTINGS_TAB_NAME:
        switch (tagName) {
          case GlobalConstants.SIZE_TAG_NAME:
            this.setActiveInStorageBySelection(GlobalConstants.CONDUIT_SETTINGS_SIZE_DATA_SELECTION_NAME, value);
            break;

          case GlobalConstants.ELEVATION_TAG_NAME:
            this.setActiveInStorageBySelection(GlobalConstants.CONDUIT_SETTINGS_ELEVATION_DATA_SELECTION_NAME, value);
            break;

          case GlobalConstants.ITEM_NUMBER_TAG_NAME:
            this.setActiveInStorageBySelection(GlobalConstants.CONDUIT_SETTINGS_ITEM_NUMBER_DATA_SELECTION_NAME, value);
            break;

          case GlobalConstants.SPOOL_TAG_NAME:
            this.setActiveInStorageBySelection(GlobalConstants.CONDUIT_SETTINGS_SPOOL_DATA_SELECTION_NAME, value);
            break;

          default:
            break;
        }
    }
  }

  public setDefaultSelectionToStorageByTabNameAndAnnotationName(
    currentXmlAnnotation: Annotation,
    tabName: string,
    annotationName: string,
    selectionName: string
  ): void {
    if (tabName === '' || annotationName === '') {
      return;
    }
    const revitVersion = this.localStorageService.getStorage(GlobalConstants.REVIT_VERSION) as string;
    const defaultSelection: Annotation = currentXmlAnnotation ?? new Annotation(tabName, annotationName, revitVersion);
    this.localStorageService.updateStorage(selectionName, defaultSelection);
  }

  public getTagsStateFromStorageByTabNameAndAnnotationName(tabName: string, annotationName: string): string {
    if (tabName === '' || annotationName === '') {
      return '';
    }

    const tagsState =
      tabName === GlobalConstants.CABLE_TRAY_TAB_NAME
        ? (this.localStorageService.getStorage(GlobalConstants.CABLE_TRAY_TAGS_STATE) as TagsState)
        : (this.localStorageService.getStorage(GlobalConstants.CONDUIT_SETTINGS_TAGS_STATE) as TagsState);
    switch (annotationName) {
      case GlobalConstants.SIZE_ANNOTATION_NAME:
        return tagsState?.sizeTag ? GlobalConstants.TRUE : GlobalConstants.FALSE;

      case GlobalConstants.ELEVATION_ANNOTATION_NAME:
        return tagsState?.elevationTag ? GlobalConstants.TRUE : GlobalConstants.FALSE;

      case GlobalConstants.ITEM_NUMBER_ANNOTATION_NAME:
        return tagsState?.itemNumberTag ? GlobalConstants.TRUE : GlobalConstants.FALSE;

      case GlobalConstants.SPOOL_ANNOTATION_NAME:
        return tagsState?.spoolTag ? GlobalConstants.TRUE : GlobalConstants.FALSE;

      default:
        return '';
    }
  }

  public getTagsStateFromStorageByTabName(tabName: string): TagsState {
    if (tabName === '') {
      return new TagsState();
    }
    return tabName === GlobalConstants.CABLE_TRAY_TAB_NAME
      ? (this.localStorageService.getStorage(GlobalConstants.CABLE_TRAY_TAGS_STATE) as TagsState)
      : (this.localStorageService.getStorage(GlobalConstants.CONDUIT_SETTINGS_TAGS_STATE) as TagsState);
  }

  public getDataToLoadByTabAndAnnonationName(tabName: string, annotationName: string, dataSelectionName: string): Annotation {
    //retrieve data selections from local storage
    const dataSelection = this.getTagDataFromLocalStorageByTagName(dataSelectionName);

    if (dataSelection !== null) {
      return dataSelection;
    }
    //get data to load for each tag based on the selection
    const dataFromXml = this.getTagDataFromXmlByTabNameAndAnnotationName(tabName, annotationName);
    this.setDefaultSelectionToStorageByTabNameAndAnnotationName(
      dataFromXml,
      tabName,
      annotationName,
      dataSelectionName
    );

    return dataFromXml;
  }

  public setActiveInStorageBySelection(selectionName: string, value: string): void {
    // Step 1: Fetch the object from local storage
    const annotation = this.localStorageService.getStorage(selectionName) as Annotation;
    // Step 2: Update the desired property if the selection exists
    if (annotation === null) {
      return;
    }
    annotation.active = value;
    // Step 3: Store the updated object back into local storage
    this.localStorageService.updateStorage(selectionName, annotation);
  }

  public setPlacementValueToStorage(dataObjName: string, value: string): void {
    if (dataObjName === '' || value === null) {
      return;
    }
    // Step 1: Fetch the object from local storage
    const annotation = this.localStorageService.getStorage(dataObjName) as Annotation;
    // Step 2: Update the desired property
    annotation.location = value;
    // Step 3: Store the updated object back into local storage
    this.localStorageService.updateStorage(dataObjName, annotation);
  }

  public setOffsetValueToStorage(dataObjName: string, label: string, value: number): void {
    if (dataObjName === '' || label === '' || value == null) {
      return;
    }
    // Step 1: Fetch the object from local storage
    const annotation = this.localStorageService.getStorage(dataObjName) as Annotation;
    // Step 2: Update the desired property
    if (label === GlobalConstants.X_OFFSET) {
      annotation.xOffset = value.toString();
    } else {
      annotation.yOffset = value.toString();
    }
    // Step 3: Store the updated object back into local storage
    this.localStorageService.updateStorage(dataObjName, annotation);
  }

  public setOccurrenceValueToStorage(dataObjName: string, value: string): void {
    if (dataObjName === '' || value === null) {
      return;
    }
    // Step 1: Fetch the object from local storage
    const annotation = this.localStorageService.getStorage(dataObjName) as Annotation;
    // Step 2: Update the desired property
    switch (value) {
      case GlobalConstants.AT_SIZE_CHANGE:
        annotation.option = GlobalConstants.SIZE_CHANGE;
        break;

      case GlobalConstants.AT_ELEVATION_CHANGE:
        annotation.option = GlobalConstants.ELEVATION_CHANGE;
        break;

      case GlobalConstants.UPON_SIZE_OR_DIRECTION_CHANGE:
        annotation.option = GlobalConstants.DIRECTION;
        break;

      default:
        annotation.option = '';
        break;
    }
    // Step 3: Store the updated object back into local storage
    this.localStorageService.updateStorage(dataObjName, annotation);
  }

  public setPropertiesValueToStorage(dataObjName: string, label: string, value: boolean): void {
    if (dataObjName === '' || label === '') {
      return;
    }
    // Step 1: Fetch the object from local storage
    const annotation = this.localStorageService.getStorage(dataObjName) as Annotation;
    // Step 2: Update the desired property
    if (label === GlobalConstants.SHOW_LEADER) {
      annotation.showLeader = value ? GlobalConstants.TRUE : GlobalConstants.FALSE;
    } else if (label === GlobalConstants.HORIZONTAL_TEXT) {
      annotation.horizontal = value ? GlobalConstants.TRUE : GlobalConstants.FALSE;
    } else if (label === GlobalConstants.SHOW_BORDER) {
      annotation.showBorder = value ? GlobalConstants.TRUE : GlobalConstants.FALSE;
    } else if (label === GlobalConstants.PER_SPOOL) {
      annotation.option = value ? GlobalConstants.ONE_ITEM : '';
    } else {
      return;
    }
    // Step 3: Store the updated object back into local storage
    this.localStorageService.updateStorage(dataObjName, annotation);
  }

  public setIgnoreStraightsValueToStorage(dataObjName: string, value: number): void {
    if (dataObjName === '' || value == null) {
      return;
    }
    // Step 1: Fetch the object from local storage
    const annotation = this.localStorageService.getStorage(dataObjName) as Annotation;
    // Step 2: Update the desired property
    annotation.ignoreLength = value.toString();
    // Step 3: Store the updated object back into local storage
    this.localStorageService.updateStorage(dataObjName, annotation);
  }

  public setTagPathValueToStorage(dataObjName: string, value: string): void {
    if (dataObjName === '' || value === null) {
      return;
    }
    // Step 1: Fetch the object from local storage
    const annotation = this.localStorageService.getStorage(dataObjName) as Annotation;
    // Step 2: Update the desired property
    annotation.tagPath = value;
    // Step 3: Store the updated object back into local storage
    this.localStorageService.updateStorage(dataObjName, annotation);
  }

  public setFittingTagPathValueToStorage(dataObjName: string, value: string): void {
    if (dataObjName === '' || value === null) {
      return;
    }
    // Step 1: Fetch the object from local storage
    const annotation = this.localStorageService.getStorage(dataObjName) as Annotation;
    // Step 2: Update the desired property
    annotation.fittingTagPath = value;
    // Step 3: Store the updated object back into local storage
    this.localStorageService.updateStorage(dataObjName, annotation);
  }
}
